"use client"

import { useEffect, useRef, useState } from "react"
import { Camera, ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"
import { trackEvent } from "@/lib/analytics"

interface PhotoTipsProps {
  /** Analytics surface for `camera_tips_shown` and `camera_tips_opened`. */
  surface?: string
  className?: string
}

const PHOTO_TIPS = [
  "Fill the frame with the affected area",
  "Use daylight",
  "Stand 2 to 3 feet away",
  "Avoid shadows",
]

export function PhotoTips({ surface = "diagnosis_failed", className }: PhotoTipsProps) {
  // The tips render expanded, so the panel is an impression surface rather than
  // a disclosure. `camera_tips_opened` therefore stays reserved for a genuine
  // user expand; firing it on a default-open mount would invert it, counting
  // only the users who collapsed the panel first.
  const [open, setOpen] = useState(true)
  const impressionSentRef = useRef(false)

  useEffect(() => {
    if (impressionSentRef.current) return
    impressionSentRef.current = true
    trackEvent("camera_tips_shown", { surface })
  }, [surface])

  const handleToggle = () => {
    setOpen((current) => {
      const next = !current
      if (next) {
        trackEvent("camera_tips_opened", { surface })
      }
      return next
    })
  }

  return (
    <div className={cn("rounded-xl border border-gray-200 bg-white", className)}>
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="inline-flex min-w-0 items-center gap-2 text-sm font-semibold text-gray-900">
          <Camera className="h-4 w-4 flex-shrink-0 text-gray-500" aria-hidden="true" />
          How to take a better photo
        </span>
        <ChevronDown
          className={cn("h-4 w-4 flex-shrink-0 text-gray-400 transition-transform", open && "rotate-180")}
          aria-hidden="true"
        />
      </button>
      {open && (
        <ul className="list-disc space-y-1.5 border-t border-gray-100 py-3 pl-9 pr-4 text-sm text-gray-700 marker:text-gray-400">
          {PHOTO_TIPS.map((tip) => (
            <li key={tip} className="pl-1">
              {tip}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
